import React, { useState, useEffect, useRef } from 'react';
import { Formik } from 'formik';
import { TextField } from '@material-ui/core';
import { Link, useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import ReCAPTCHA from 'react-google-recaptcha';
import { actions } from '../../store/ducks/auth/actions';
import { EMAIL_REGEX } from '../../constants/regex';
import { ArrowBack as ArrowBackIcon } from '@material-ui/icons';
import { AuthWrapper } from 'app/Wrappers/AuthWrapper';
import config from "config";
import { 
  executeRecaptcha, 
  createOnRecaptchaExpired, 
  createOnRecaptchaErrored, 
  handleRecaptchaError 
} from '../../utils/recaptchaUtils';

const ForgotPassword = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const intl = useIntl();
  const recaptchaRef = useRef();
  const mountedRef = useRef(true);
  const executingCaptchaRef = useRef(false);
  
  const { errorset, loading } = useSelector(
    state => ({
      errorset: state.auth.forgotPassError,
      loading: state.auth.loading
    }),
    shallowEqual
  );
  
  const [isRequested, setIsRequested] = useState(false);
  const [isExecutingCaptcha, setIsExecutingCaptcha] = useState(false);

  useEffect(() => {
    dispatch(actions.forgotPassError(''));
    return () => {
      mountedRef.current = false;
      dispatch(actions.forgotPassError(''));
    };
  }, [dispatch]);

  // Create reCAPTCHA event handlers using shared utility
  const onRecaptchaExpired = createOnRecaptchaExpired({
    recaptchaRef,
    dispatch,
    errorAction: actions.forgotPassError
  });

  const onRecaptchaErrored = createOnRecaptchaErrored({
    recaptchaRef,
    dispatch,
    errorAction: actions.forgotPassError
  });

  const forgotPassword = async (values, { setStatus, setSubmitting }) => {
    const { email } = values;

    try {
      let recaptchaToken = null;

      if (config.recaptcha.ENABLE_GOOGLE_RECAPTCHA) {
        try {
          recaptchaToken = await executeRecaptcha({
            recaptchaRef,
            mountedRef,
            executingCaptchaRef,
            setIsExecutingCaptcha,
            dispatch,
            errorAction: actions.forgotPassError
          });
        } catch (e) {
          handleRecaptchaError(e, dispatch, actions.forgotPassError);
          setSubmitting(false);
          return;
        }
        if (!recaptchaToken) {
          dispatch(actions.forgotPassError('reCAPTCHA verification failed. Please try again.'));
          setSubmitting(false);
          return;
        }
      }

      const callback = (success) => {
        try {
          if (success) {
            setIsRequested(true);
            setSubmitting(false);
          } else {
            setSubmitting(false);
          }
        } catch (err) {
          setSubmitting(false);
          setStatus('An unexpected error occurred. Please try again.');
        }
      };

      dispatch(actions.forgotPassword({ email, recaptchaToken }, callback));
    } catch (error) {
      dispatch(actions.forgotPassError('An error occurred during password reset request. Please try again.'));
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (isRequested) {
      setIsRequested(false);
      history.push('/auth/send-verification');
    }
  }, [history, isRequested]);

  return (
    <AuthWrapper>
      <div className="login-form-title">
        <FormattedMessage id="AUTH.FORGOT.TITLE" />
      </div>
      <div className="login-form-summary">
        <FormattedMessage id="AUTH.FORGOT.DESC" />
      </div>
      <div className="kt-login__body" style={{ position: 'relative' }}>
        <div className="kt-login__form">
          <Formik
            initialValues={{ email: '' }}
            validate={values => {
              const errors = {};

              if (!values.email) {
                errors.email = intl.formatMessage({
                  id: 'AUTH.VALIDATION.REQUIRED_FIELD',
                });
              } else if (!EMAIL_REGEX.test(values.email)) {
                errors.email = intl.formatMessage({
                  id: 'AUTH.VALIDATION.INVALID_FIELD',
                });
              }

              return errors;
            }}
            onSubmit={forgotPassword}
          >
            {({
              values,
              status,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit} className="kt-form">
                {config.recaptcha.ENABLE_GOOGLE_RECAPTCHA && (
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    size="invisible"
                    sitekey={config.recaptcha.SITE_KEY}
                    onErrored={onRecaptchaErrored}
                    onExpired={onRecaptchaExpired}
                  />
                )}

                {errorset ? (
                  <div role="alert" className="alert alert-danger">
                    <div className="alert-text">{errorset}</div>
                  </div>
                ) : (
                  status && (
                    <div role="alert" className="alert alert-danger">
                      <div className="alert-text">{status}</div>
                    </div>
                  )
                )}

                <div className="form-group">
                  <div className="label">Email</div>
                  <TextField
                    type="email"
                    margin="normal"
                    fullWidth={true}
                    name="email"
                    className="kt-width-full custom-form-label"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.email}
                    helperText={touched.email && errors.email}
                    error={Boolean(touched.email && errors.email)}
                    placeholder="Enter email"
                  />
                </div>

                <div className="login-form-actions">
                  <button
                    type="submit"
                    className={`btn button-login ${loading || isExecutingCaptcha ? 'kt-spinner kt-spinner--right kt-spinner--md kt-spinner--light' : ''}`}
                    disabled={isSubmitting || !values.email || loading || isExecutingCaptcha}
                  >
                    Reset
                  </button>
                </div>
              </form>
            )}
          </Formik>
        </div>
        <div className="kt-login__back">
          <Link to="/auth">
            <ArrowBackIcon />
          </Link>
          <span>Back to Sign in</span>
        </div>
      </div>
    </AuthWrapper>
  );
};

export default ForgotPassword;
